"use client";

import { ChevronRight, Search } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { getMockHelpArticles } from "@/dummy";
import {
  useVisitorHelpArticlesByIdsSlimQuery,
  useVisitorHelpListSlimQuery,
  VisitorHelpArticleSort,
} from "@/lib/graphql/queries/generated/help.generated";
import { cacheKeys } from "@/lib/offline-cache";
import { useDemoContent } from "@/lib/use-demo-translator";
import { useOfflineData } from "@/lib/use-offline-data";
import { useWidgetEnv } from "@/lib/use-widget-env";
import type { HomeBlock, WidgetTab } from "@/lib/widget-config";
import { useWidgetInstance } from "@/lib/widget-instance-context";

/** Skeleton suggestion row — flat row inside the card, matches the
 *  rendered row layout so the card doesn't reflow when articles
 *  arrive. */
function SuggestionSkeleton() {
  return (
    <div className="flex w-full items-center gap-2 px-3.5 py-2.5">
      <div className="h-3.5 min-w-0 flex-1 animate-pulse rounded-wx-sm bg-wx-bg-elevated-2" />
      <div className="h-3.5 w-3.5 shrink-0 animate-pulse rounded-wx-sm bg-wx-bg-elevated-2" />
    </div>
  );
}

/** One article suggestion row inside the help-search card. Flat
 *  layout (no individual border) — the surrounding card provides the
 *  frame; rows just hover-highlight on tap. Title left, chevron right. */
function SuggestionRow({
  title,
  slug,
  onOpenArticle,
  onOpenTab,
}: {
  title: string;
  slug: string;
  onOpenArticle?: (slug: string) => void;
  onOpenTab: (tab: WidgetTab) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => (onOpenArticle ? onOpenArticle(slug) : onOpenTab("help"))}
      className="flex w-full items-center gap-2 px-3.5 py-2.5 text-start transition-colors hover:bg-wx-bg-elevated-2"
    >
      <span className="min-w-0 flex-1 truncate text-sm text-wx-fg">{title}</span>
      <ChevronRight size={14} className="shrink-0 text-wx-fg-subtle rtl:-scale-x-100" />
    </button>
  );
}

/**
 * Home-tab `help-search` block — operator-configured search affordance
 * with optional article suggestions above it.
 *
 * Suggestions resolve per `block.suggestions.mode`:
 *   - **popular**  → `visitorHelpList({ sort: POPULAR, limit })`
 *   - **recent**   → `visitorHelpList({ sort: RECENT, limit })`
 *   - **category** → `visitorHelpList({ folderId: categoryId, limit })`
 *   - **manual**   → `visitorHelpArticlesByIds(articleIds)` (preserves order)
 *
 * Demo mode renders `getMockHelpArticles` with mode-appropriate sort
 * (no network). Dashboard preview short-circuits to `env.previewData`
 * when present (avoids cross-origin BE calls from the dashboard).
 * Tap any row → `onOpenArticle(slug)` drills into the article.
 */
export function HelpSearchBlock({
  block,
  onOpenTab,
  onOpenArticle,
  isDummy,
}: {
  block: Extract<HomeBlock, { kind: "help-search" }>;
  onOpenTab: (tab: WidgetTab) => void;
  onOpenArticle?: (slug: string) => void;
  isDummy: boolean;
}) {
  const t = useTranslations("home");
  const tDemo = useDemoContent();
  const env = useWidgetEnv();
  const locale = useLocale();

  const suggestions = block.suggestions;
  // Per-mode item cap:
  //  - **manual** → the operator explicitly listed N article ids; that
  //    list IS the answer, so use `articleIds.length` instead of the
  //    abstract `maxItems` knob (which is irrelevant when the operator
  //    has hand-picked rows). Capped at 10 for UI sanity — a runaway
  //    list of 50 articles would dominate the home tab.
  //  - **popular / recent / category** → no explicit ids, the BE
  //    returns a sorted feed, so `maxItems` is the only knob. Default
  //    to 0 (skip the query) when the operator omitted it.
  //  - **legacy / unknown** → 0 (skip).
  const limit = suggestions
    ? suggestions.mode === "manual"
      ? Math.min((suggestions.articleIds ?? []).length, 10)
      : Math.max(0, Math.min(suggestions.maxItems ?? 0, 10))
    : 0;

  // Dashboard-preview short-circuit — the dashboard's prefetched
  // article rows live in `env.previewData.helpArticles`. We can't read
  // by-id (the lookup is keyed differently for each mode), so for the
  // preview pane we always sort/slice the prefetched list locally.
  const previewArticles = env.previewData?.helpArticles;
  const usePreview = !isDummy && !!previewArticles && limit > 0;

  // Build the args once — passing identical objects keeps Apollo from
  // re-issuing the request on every parent re-render.
  const listSort: VisitorHelpArticleSort | undefined =
    suggestions?.mode === "popular"
      ? VisitorHelpArticleSort.Popular
      : suggestions?.mode === "recent"
        ? VisitorHelpArticleSort.Recent
        : undefined;
  const listFolderId = suggestions?.mode === "category" ? suggestions.categoryId : undefined;
  const skipList =
    isDummy ||
    usePreview ||
    limit === 0 ||
    !suggestions ||
    !(
      suggestions.mode === "popular" ||
      suggestions.mode === "recent" ||
      suggestions.mode === "category"
    ) ||
    (suggestions.mode === "category" && !listFolderId);

  const { data: listData, loading: listLoading } = useVisitorHelpListSlimQuery({
    variables: {
      args: {
        limit,
        locale,
        sort: listSort,
        folderId: listFolderId ?? undefined,
        // Recursive scan so a top-level category with subfolders surfaces
        // articles from the whole tree, not just the ones pinned at the
        // root. Operators typically pick the broadest category that
        // covers what they want — without `recursive` the row stays
        // empty whenever articles live in subfolders.
        recursive: suggestions?.mode === "category" ? true : undefined,
      },
    },
    skip: skipList,
  });

  const manualIds =
    suggestions?.mode === "manual" ? (suggestions.articleIds ?? []).slice(0, limit) : [];
  const skipManual = isDummy || usePreview || manualIds.length === 0;
  const { data: byIdsData, loading: byIdsLoading } = useVisitorHelpArticlesByIdsSlimQuery({
    variables: { ids: manualIds, locale },
    skip: skipManual,
  });

  // Offline READ: cache both suggestion sources so the home help-search block
  // still shows its rows when the BE is down.
  const helpPk = useWidgetInstance().publicKey;
  const offlineOn = !!helpPk && !isDummy && !usePreview;
  const effectiveListData = useOfflineData(
    offlineOn && !skipList
      ? cacheKeys.home(
          helpPk as string,
          `help-list-slim:${suggestions?.mode ?? ""}:${limit}:${listFolderId ?? ""}`,
          locale,
        )
      : null,
    listData,
  );
  const effectiveByIdsData = useOfflineData(
    offlineOn && !skipManual
      ? cacheKeys.home(helpPk as string, `help-ids-slim:${manualIds.join(",")}`, locale)
      : null,
    byIdsData,
  );

  // Resolve the rendered rows. Demo, preview, and live paths all
  // produce a `{ title, slug }[]` so the JSX below is uniform.
  const rows: { id: string; title: string; slug: string }[] = (() => {
    if (limit === 0 || !suggestions) return [];

    if (isDummy) {
      const all = getMockHelpArticles(tDemo);
      const sorted =
        suggestions.mode === "popular"
          ? all.slice().sort((a, b) => b.viewCount - a.viewCount)
          : suggestions.mode === "recent"
            ? all.slice().sort((a, b) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""))
            : suggestions.mode === "category" && suggestions.categoryId
              ? all.filter((a) => a.categoryId === suggestions.categoryId)
              : suggestions.mode === "manual"
                ? (suggestions.articleIds ?? [])
                    .map((id) => all.find((a) => a.id === id))
                    .filter((a): a is NonNullable<typeof a> => a != null)
                : all;
      return sorted.slice(0, limit).map((a) => ({ id: a.id, title: a.title, slug: a.slug }));
    }

    if (usePreview && previewArticles) {
      // Operator-side rows the dashboard prefetched. Each one is a
      // BE-shaped HelpArticle JSON, accepted as-is.
      const parsed = previewArticles
        .map((raw) => {
          if (!raw || typeof raw !== "object") return null;
          const r = raw as { _id?: unknown; title?: unknown; slug?: unknown };
          if (
            typeof r._id === "string" &&
            typeof r.title === "string" &&
            typeof r.slug === "string"
          ) {
            return { id: r._id, title: r.title, slug: r.slug };
          }
          return null;
        })
        .filter((a): a is NonNullable<typeof a> => a != null);
      // Honour `manual` ordering when the operator pinned specific ids.
      if (suggestions.mode === "manual" && suggestions.articleIds) {
        const byId = new Map(parsed.map((a) => [a.id, a]));
        return suggestions.articleIds
          .map((id) => byId.get(id))
          .filter((a): a is NonNullable<typeof a> => a != null)
          .slice(0, limit);
      }
      return parsed.slice(0, limit);
    }

    if (suggestions.mode === "manual") {
      const items = effectiveByIdsData?.visitorHelpArticlesByIds ?? [];
      // `visitorHelpArticlesByIds` returns rows in request order; nulls
      // are stripped here so a deleted/unpublished id doesn't render an
      // empty row.
      return items
        .filter((a): a is NonNullable<typeof a> => a != null)
        .map((a) => ({ id: a._id, title: a.title, slug: a.slug }));
    }

    const items = effectiveListData?.visitorHelpList?.items ?? [];
    return items.map((a) => ({ id: a._id, title: a.title, slug: a.slug }));
  })();

  const loading = !isDummy && !usePreview && (listLoading || byIdsLoading);
  const skeletonCount = loading && rows.length === 0 ? limit : 0;

  const hasSuggestions = skeletonCount > 0 || rows.length > 0;
  const placeholder = block.placeholder?.trim() || t("searchPlaceholder");

  return (
    <div className="overflow-hidden rounded-wx-lg border border-wx-border bg-wx-bg-elevated">
      {/* Search affordance lives at the TOP of the card; suggestions
          (curated options) sit BELOW it as flat rows. Matches the
          familiar "one card, search + a few quick picks" look —
          the visitor reaches for the search first, falls through to
          the picks if nothing's pre-formed in their head. */}
      <button
        type="button"
        onClick={() => onOpenTab("help")}
        className="flex w-full items-center gap-2.5 px-3.5 py-3 text-start transition-colors hover:bg-wx-bg-elevated-2"
      >
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-wx-fg">
          {placeholder}
        </span>
        <Search size={16} className="shrink-0 text-wx-fg-muted" />
      </button>
      {hasSuggestions && (
        <div className="border-t border-wx-border">
          {skeletonCount > 0
            ? Array.from({ length: skeletonCount }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: pure layout placeholder
                <SuggestionSkeleton key={i} />
              ))
            : rows.map((row) => (
                <SuggestionRow
                  key={row.id}
                  title={row.title}
                  slug={row.slug}
                  onOpenArticle={onOpenArticle}
                  onOpenTab={onOpenTab}
                />
              ))}
        </div>
      )}
    </div>
  );
}
